import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import { notificationsAPI } from '../services/api';
import MobileNotificationsPage from '../components/mobile/MobileNotificationsPage';
import { 
  Bell, 
  CheckCheck,
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Info,
  Gift,
  Megaphone,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2
} from 'lucide-react';

const NotificationsPage = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, deposit, withdrawal, trade, etc.
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [filter, page]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = filter === 'unread' 
        ? await notificationsAPI.getUnreadOnly(100)
        : await notificationsAPI.getAll(100, 0);
      let data = res.data || [];
      
      // Filter by type
      if (filter && filter !== 'all' && filter !== 'unread') {
        data = data.filter(n => n.type === filter);
      }
      
      // Filter by search query
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        data = data.filter(n => 
          n.title?.toLowerCase().includes(query) ||
          n.message?.toLowerCase().includes(query)
        );
      }
      
      setTotalPages(Math.ceil(data.length / pageSize));
      setNotifications(data.slice((page - 1) * pageSize, page * pageSize));
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Mobile version
  if (isMobile) {
    return <MobileNotificationsPage />;
  }

  const getIcon = (type) => {
    const iconProps = { size: 20 };
    switch (type) {
      case 'deposit':
        return <ArrowDownRight {...iconProps} color="#0ecb81" />;
      case 'withdrawal':
        return <ArrowUpRight {...iconProps} color="#f6465d" />;
      case 'trade':
      case 'position':
      case 'take_profit':
        return <TrendingUp {...iconProps} color="#0ecb81" />;
      case 'stop_loss':
        return <TrendingDown {...iconProps} color="#f6465d" />;
      case 'bonus':
      case 'promo':
        return <Gift {...iconProps} color="#f0b90b" />;
      case 'announcement':
        return <Megaphone {...iconProps} color="#8b5cf6" />;
      case 'warning':
        return <AlertCircle {...iconProps} color="#f0b90b" />;
      case 'error':
        return <AlertCircle {...iconProps} color="#f6465d" />;
      default:
        return <Info {...iconProps} color="#1e90ff" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    return date.toLocaleDateString('ru-RU', { 
      day: 'numeric', 
      month: 'short', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const filterOptions = [
    { value: 'all', label: 'Все' },
    { value: 'unread', label: 'Непрочитанные' },
    { value: 'deposit', label: 'Депозиты' },
    { value: 'withdrawal', label: 'Выводы' },
    { value: 'trade', label: 'Сделки' },
    { value: 'take_profit', label: 'Take Profit' },
    { value: 'stop_loss', label: 'Stop Loss' },
    { value: 'announcement', label: 'Объявления' },
  ];

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px' 
      }}>
        <div>
          <h1 style={{ 
            color: COLORS.text, 
            fontSize: '24px', 
            fontWeight: '700',
            marginBottom: '4px' 
          }}>
            Уведомления
          </h1>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            {unreadCount > 0 ? `${unreadCount} непрочитанных` : 'Все прочитано'}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: COLORS.primary,
              border: 'none',
              borderRadius: '8px',
              color: '#000',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            <CheckCheck size={18} />
            Прочитать все
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        {/* Search */}
        <div style={{ 
          flex: 1, 
          minWidth: '200px',
          position: 'relative' 
        }}>
          <Search 
            size={18} 
            style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: COLORS.textSecondary 
            }} 
          />
          <input
            type="text"
            placeholder="Поиск..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
            }}
          />
        </div>

        {/* Filter dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Filter size={18} color={COLORS.textSecondary} />
          <select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            style={{
              padding: '10px 12px',
              backgroundColor: COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '8px',
              color: COLORS.text,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {filterOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ 
            padding: '60px', 
            textAlign: 'center', 
            color: COLORS.textSecondary 
          }}>
            Загрузка...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ 
            padding: '60px', 
            textAlign: 'center', 
            color: COLORS.textSecondary 
          }}>
            <Bell size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>Нет уведомлений</p>
            <p style={{ fontSize: '13px', opacity: 0.7 }}>
              {filter !== 'all' ? 'Попробуйте изменить фильтр' : 'Здесь будут ваши уведомления'}
            </p>
          </div>
        ) : (
          notifications.map((notification, index) => (
            <div
              key={notification.id}
              onClick={() => !notification.is_read && markAsRead(notification.id)}
              style={{
                padding: '16px 20px',
                borderBottom: index < notifications.length - 1 ? `1px solid ${COLORS.border}20` : 'none',
                backgroundColor: notification.is_read ? 'transparent' : `${COLORS.primary}05`,
                cursor: notification.is_read ? 'default' : 'pointer',
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
                transition: 'background-color 0.2s',
              }}
            >
              {/* Icon */}
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                backgroundColor: COLORS.background,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {getIcon(notification.type)}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '4px',
                }}>
                  <span style={{
                    color: COLORS.text,
                    fontSize: '15px',
                    fontWeight: notification.is_read ? '400' : '600',
                  }}>
                    {notification.title}
                  </span>
                  <span style={{
                    color: COLORS.textSecondary,
                    fontSize: '12px',
                    flexShrink: 0,
                    marginLeft: '12px',
                  }}>
                    {formatDate(notification.created_at)}
                  </span>
                </div>
                {notification.message && (
                  <p style={{
                    color: COLORS.textSecondary,
                    fontSize: '14px',
                    lineHeight: '1.5',
                    margin: 0,
                  }}>
                    {notification.message}
                  </p>
                )}
              </div>

              {/* Unread indicator */}
              {!notification.is_read && (
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: COLORS.primary,
                  flexShrink: 0,
                  marginTop: '8px',
                }} />
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          marginTop: '20px',
        }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 12px',
              backgroundColor: page === 1 ? COLORS.background : COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '6px',
              color: page === 1 ? COLORS.textSecondary : COLORS.text,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronLeft size={18} />
          </button>
          
          <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Страница {page} из {totalPages}
          </span>
          
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '8px 12px',
              backgroundColor: page === totalPages ? COLORS.background : COLORS.card,
              border: `1px solid ${COLORS.border}`,
              borderRadius: '6px',
              color: page === totalPages ? COLORS.textSecondary : COLORS.text,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ChevronRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
